// src/pages/WorkoutJournalPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box, Button, Collapse, Flex, Heading, IconButton, Input, Spinner, Stack, Text,
    useColorModeValue, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalCloseButton, ModalBody, chakra
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { format, parse, subDays, isWithinInterval } from 'date-fns';
import { motion } from 'framer-motion';
import { SupabaseClient } from '../../supabaseClient';
import WorkoutEntryForm from './WorkoutEntryForm';
import { useAuth } from '../../contexts/AuthContext';

const MotionBox = motion(chakra.div);
const MotionHeading = motion(Heading);

// combine workout_sessions and rehab_workouts into unified logs
const groupByDate = (logs) =>
    logs.reduce((acc, entry) => {
        const key = entry.workout_date;
        if (!acc[key]) acc[key] = [];
        acc[key].push(entry);
        return acc;
    }, {});

const WorkoutJournalPage = () => {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [openSections, setOpenSections] = useState({});
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [entryToEdit, setEntryToEdit] = useState(null);

    const bg = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.700');
    const titleColor = useColorModeValue('gray.800', 'white');
    const descColor = useColorModeValue('gray.600', 'gray.300');
    const boxColor = useColorModeValue('gray.200', 'gray.600');

    const fetchLogs = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        // fetch regular workouts
        const { data: sessions = [], error: sessionError } = await SupabaseClient
            .from('workout_sessions')
            .select('*, strength_exercises(*), cardio_exercises(*)')
            .eq('profile_id', user.id)
            .order('workout_date', { ascending: false })
            .order('created_at', { ascending: false });

        // fetch rehab workouts
        const { data: rehab = [], error: rehabError } = await SupabaseClient
            .from('rehab_workouts')
            .select('*')
            .eq('profile_id', user.id)
            .order('workout_date', { ascending: false })
            .order('created_at', { ascending: false });

        if (!sessionError && !rehabError) {
            // tag and merge
            const sessionLogs = sessions.map(s => ({ ...s, logType: 'workout' }));
            const rehabLogs = rehab.map(r => ({ ...r, logType: 'rehab' }));
            setLogs([...sessionLogs, ...rehabLogs]);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const logsToDisplay = useMemo(() => {
        const end = parse(selectedDate, 'yyyy-MM-dd', new Date());
        const start = subDays(end, 6);
        return logs.filter(entry =>
            isWithinInterval(parse(entry.workout_date, 'yyyy-MM-dd', new Date()), { start, end })
        );
    }, [logs, selectedDate]);

    const groupedLogs = useMemo(() => groupByDate(logsToDisplay), [logsToDisplay]);

    const toggleSection = (id) => {
        setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (loading) return (
        <Box textAlign="center" mt={10} bg={bg} minH="100vh" p={8}>
            <Spinner size="xl" />
            <Text mt={4} color={descColor}>Loading logs…</Text>
        </Box>
    );

    return (
        <Box as="main" bg={bg} minH="100vh" py={10} px={4}>
            <MotionHeading textAlign="center" color={titleColor} fontSize={{ base: '3xl', md: '4xl' }} mb={8}
                           initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                🏋️ Workout & Rehab Journal
            </MotionHeading>

            <Flex mb={10} justify="space-between" align={{ base: 'stretch', md: 'center' }} flexDir={{ base: 'column', md: 'row' }} gap={4} maxW="3xl" mx="auto">
                <Box>
                    <Text mb={2} color={descColor}>Select week ending date:</Text>
                    <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={format(new Date(), 'yyyy-MM-dd')} bg={cardBg} />
                </Box>
                <Button colorScheme="teal" onClick={() => { setEntryToEdit(null); onOpen(); }}>
                    Add Workout
                </Button>
            </Flex>

            {Object.entries(groupedLogs).sort((a, b) => b[0].localeCompare(a[0])).map(([date, entries]) => (
                <MotionBox key={date} bg={cardBg} p={8} rounded="2xl" shadow="xl" mb={10}
                           initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Heading size="md" mb={6} color={titleColor}>{format(parse(date, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')}</Heading>
                    <Stack spacing={6}>
                        {entries.map(entry => (
                            <Box key={entry.id} p={6} bg={bg} borderRadius="lg" border="1px solid" borderColor={boxColor}>
                                <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                                    <Box flex="1 1 60%">
                                        <Text fontWeight="bold" color={titleColor}>
                                            {entry.logType === 'workout' ? entry.workout_type.toUpperCase() + ' Workout' : 'Rehab Session'}
                                        </Text>
                                        {entry.logType === 'rehab' && (
                                            <Text mt={2} color={descColor}>
                                                Target: {entry.area_targeted}, Pain lvl: {entry.pain_level}, Duration: {entry.duration_minutes} min
                                            </Text>
                                        )}
                                        {entry.notes && <Text mt={2} color={descColor}>{entry.notes}</Text>}
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <IconButton size="sm" icon={<EditIcon />} aria-label="Edit" onClick={() => { setEntryToEdit(entry); onOpen(); }} />
                                        <IconButton size="sm" icon={<DeleteIcon />} aria-label="Delete" onClick={async () => {
                                            const table = entry.logType === 'rehab' ? 'rehab_workouts' : 'workout_sessions';
                                            await SupabaseClient.from(table).delete().eq('id', entry.id);
                                            setLogs(prev => prev.filter(e => e.id !== entry.id));
                                        }} />
                                        <Button size="sm" variant="outline" onClick={() => toggleSection(entry.id)}>
                                            {openSections[entry.id] ? "Hide Details" : "Show Details"}
                                        </Button>
                                    </Stack>
                                </Flex>

                                <Collapse in={openSections[entry.id]} animateOpacity>
                                    <Box mt={4}>
                                        {entry.logType === 'workout' && entry.strength_exercises?.length > 0 && (
                                            <>
                                                <Text fontWeight="bold" mt={2}>Strength Sets:</Text>
                                                {entry.strength_exercises.map(set => (
                                                    <Text key={set.id}>
                                                        {set.exercise_name}: {set.reps} reps × {set.weight_kg}kg
                                                    </Text>
                                                ))}
                                            </>
                                        )}
                                        {entry.logType === 'workout' && entry.cardio_exercises?.length > 0 && (
                                            <>
                                                <Text fontWeight="bold" mt={2}>Cardio Stats:</Text>
                                                {entry.cardio_exercises.map(cardio => (
                                                    <Text key={cardio.id}>
                                                        {cardio.activity_type}: {cardio.distance_km} km in {cardio.duration_minutes} min (
                                                        {cardio.avg_pace_min_per_km} min/km)
                                                    </Text>
                                                ))}
                                            </>
                                        )}
                                        {entry.logType === 'rehab' && (
                                            <>
                                                <Text fontWeight="bold" mt={2}>Rehab Details:</Text>
                                                <Text>Area: {entry.area_targeted}</Text>
                                                <Text>Pain Level: {entry.pain_level}</Text>
                                                <Text>Duration: {entry.duration_minutes} min</Text>
                                            </>
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </Stack>
                </MotionBox>
            ))}

            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent bg={cardBg}>
                    <ModalHeader>{entryToEdit ? "Edit Entry" : "Add Workout"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <WorkoutEntryForm initialEntry={entryToEdit} onSuccess={() => { fetchLogs(); onClose(); }} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default WorkoutJournalPage;
